// src/utils/analytics.ts
import { logEvent as firebaseLogEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '../config/firebase';
import { logDev, logError, logWarn } from './logger';

/**
 * Analytics 工具函數
 * 提供安全的事件追蹤功能，自動處理 Analytics 未初始化的情況
 *
 * 🔧 競態條件處理：
 * Analytics 初始化是非同步的，可能在應用啟動後 100-200ms 才完成
 * 使用事件佇列暫存早期事件，待初始化完成後重放
 */

// 事件佇列：暫存 Analytics 初始化前的事件
const eventQueue: Array<() => void> = [];
let isInitialized = false;
let initCheckInterval: ReturnType<typeof setInterval> | null = null;
const MAX_INIT_WAIT_MS = 3000; // 最多等待 3 秒
const CHECK_INTERVAL_MS = 100;

/**
 * 等待 Analytics 初始化完成
 * 初始化完成後自動重放佇列中的事件
 *
 * 🛡️ 記憶體安全：
 * - 懶加載：首次調用時才啟動檢查（避免模組載入時立即執行）
 * - 防重複：已啟動則跳過（避免 HMR 重載時重複啟動）
 * - 可清理：提供 cleanup 函數供測試和 HMR 使用
 *
 * 🔧 Bug 修復：使用區域變數 attempts 避免狀態污染
 */
const waitForAnalyticsInit = () => {
  // 🛡️ 防止重複啟動（HMR 重載保護）
  if (initCheckInterval !== null) {
    logDev('Analytics', '初始化檢查已在運行，跳過');
    return;
  }

  // 🛡️ 開發環境優化：模擬器環境直接跳過
  if (process.env.NODE_ENV === 'development' && !analytics) {
    logDev('Analytics', '開發環境且 analytics 未初始化，跳過檢查');
    return;
  }

  // ✅ 使用區域變數，避免重複調用時狀態污染
  let attempts = 0;

  initCheckInterval = setInterval(() => {
    attempts++;

    if (analytics) {
      // Analytics 已初始化
      isInitialized = true;
      if (initCheckInterval) clearInterval(initCheckInterval);
      initCheckInterval = null;

      logDev('Analytics', `初始化完成，重放 ${eventQueue.length} 個事件`);

      // 重放佇列中的事件
      eventQueue.forEach(fn => fn());
      eventQueue.length = 0;
    } else if (attempts * CHECK_INTERVAL_MS >= MAX_INIT_WAIT_MS) {
      // 超時，放棄等待
      if (initCheckInterval) clearInterval(initCheckInterval);
      initCheckInterval = null;
      logWarn('Analytics', `初始化超時（${attempts} 次嘗試），丟棄 ${eventQueue.length} 個事件`);
      eventQueue.length = 0;
    }
  }, CHECK_INTERVAL_MS);
};

/**
 * 清理函數：停止初始化檢查並重置所有狀態（供測試和 HMR 使用）
 * 🧪 測試環境和 HMR 重載時應調用此函數避免記憶體洩漏和狀態污染
 *
 * 清理內容：
 * - clearInterval：停止背景檢查
 * - 重置狀態：isInitialized, initCheckInterval
 * - 清空佇列：eventQueue
 */
export const cleanupAnalytics = () => {
  // 停止 interval
  if (initCheckInterval) {
    clearInterval(initCheckInterval);
    initCheckInterval = null;
  }

  // 重置狀態
  isInitialized = false;

  // 清空事件佇列
  const queueSize = eventQueue.length;
  eventQueue.length = 0;

  logDev('Analytics', `完全清理完成（清除 ${queueSize} 個待處理事件）`);
};

/**
 * 記錄自訂事件
 * @param eventName 事件名稱（建議使用小寫加底線，如 'user_signup'）
 * @param eventParams 事件參數（選用）
 */
export const logEvent = (eventName: string, eventParams?: Record<string, any>) => {
  const sendEvent = () => {
    if (!analytics) {
      logDev('Analytics', `未初始化，跳過事件: ${eventName}`);
      return;
    }

    try {
      // 🛡️ 深拷貝參數，避免閉包捕獲可變物件
      const safeParams = eventParams ? { ...eventParams } : undefined;
      firebaseLogEvent(analytics, eventName, safeParams);
      logDev('Analytics', `事件已記錄: ${eventName}`);
    } catch (error) {
      logError('Analytics', `記錄事件失敗: ${eventName}`, error);
    }
  };

  // 如果已初始化或確定沒有 analytics，直接執行
  if (isInitialized || analytics) {
    sendEvent();
  } else {
    // 加入佇列，等待初始化
    logDev('Analytics', `事件加入佇列: ${eventName}`);
    eventQueue.push(sendEvent);

    // 🔧 懶加載：首次調用時才啟動初始化檢查
    waitForAnalyticsInit();
  }
};

/**
 * 記錄頁面瀏覽
 * @param pagePath 頁面路徑（如 '/dashboard'）
 * @param pageTitle 頁面標題（選用）
 */
export const logPageView = (pagePath: string, pageTitle?: string) => {
  logEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

/**
 * 設定用戶 ID（登入後使用）
 * ⚠️ 注意：不要傳入 email 或其他敏感資料，只傳 userId
 * @param userId 用戶 ID（Firebase UID）
 */
export const setAnalyticsUserId = (userId: string | null) => {
  const setId = () => {
    if (!analytics) {
      logDev('Analytics', '未初始化，跳過設定 userId');
      return;
    }

    try {
      setUserId(analytics, userId);
      logDev('Analytics', 'userId 已設定');
    } catch (error) {
      logError('Analytics', '設定 userId 失敗', error);
    }
  };

  // 如果已初始化，直接執行；否則加入佇列
  if (isInitialized || analytics) {
    setId();
  } else {
    logDev('Analytics', 'setUserId 加入佇列');
    eventQueue.push(setId);

    // 🔧 懶加載：首次調用時才啟動初始化檢查
    waitForAnalyticsInit();
  }
};

/**
 * 設定用戶屬性
 * ⚠️ 注意：不要包含敏感資料（email、姓名等）
 * @param properties 用戶屬性（如 { subscription_tier: 'pro' }）
 */
export const setAnalyticsUserProperties = (properties: Record<string, any>) => {
  const setProps = () => {
    if (!analytics) {
      logDev('Analytics', '未初始化，跳過設定 userProperties');
      return;
    }

    try {
      // 🛡️ 深拷貝屬性，避免閉包捕獲可變物件
      const safeProps = { ...properties };
      setUserProperties(analytics, safeProps);
      logDev('Analytics', 'userProperties 已設定');
    } catch (error) {
      logError('Analytics', '設定 userProperties 失敗', error);
    }
  };

  // 如果已初始化，直接執行；否則加入佇列
  if (isInitialized || analytics) {
    setProps();
  } else {
    logDev('Analytics', 'setUserProperties 加入佇列');
    eventQueue.push(setProps);

    // 🔧 懶加載：首次調用時才啟動初始化檢查
    waitForAnalyticsInit();
  }
};

/**
 * 常用事件快捷函數
 */
export const analyticsEvents = {
  // 用戶行為
  userSignUp: () => logEvent('sign_up'),
  userLogin: () => logEvent('login'),
  userLogout: () => logEvent('logout'),

  // 功能使用
  createItem: (itemType: string) => logEvent('create_item', { item_type: itemType }),
  deleteItem: (itemType: string) => logEvent('delete_item', { item_type: itemType }),
  updateItem: (itemType: string) => logEvent('update_item', { item_type: itemType }),

  // 錯誤追蹤
  error: (errorCode: string, errorMessage?: string) => logEvent('error', {
    error_code: errorCode,
    error_message: errorMessage,
  }),
};
